import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  ActivityConfig,
  ActivityDataDeleteOrderResult,
  ActivityDataDeleteResult
} from '@models/activity.model';
import {
  ActivityDataToDelete,
  ActivityState,
  ActivityStep,
  SessionActivityOrder,
  StoredActivityConfig
} from '@models/activity.model';
import { PersonItem } from '@models/custom-fields.model';
import { CartInfo } from '@models/session-details';
import { ShoppingCartData } from '@models/shopping-cart.model';
import { SessionStorageService } from '@services/browser-storage/browser-storage.service';
import { NotificationsService } from '@services/notifications/notifications.service';
import { BehaviorSubject } from 'rxjs';

const CONFIG_STORAGE_KEY: string = 'ts-activity-config';
const ORDERS_STORAGE_KEY: string = 'ts-activity-orders';

const DEFAULT_STATE: ActivityState = {
  type_id: 0,
  cart_empty: true,
  display_dialog: false
};

@Injectable({
  providedIn: 'root'
})
export class ActivityStateService {
  readonly steps_subject: BehaviorSubject<ActivityStep[]> = new BehaviorSubject<ActivityStep[]>([]);
  readonly order_for_person: BehaviorSubject<PersonItem | undefined> = new BehaviorSubject<
    PersonItem | undefined
  >(undefined);
  private config: Partial<ActivityConfig> | undefined;
  private state: ActivityState = {
    ...DEFAULT_STATE
  };
  private orders: SessionActivityOrder[] = [];
  private active_step_index: number = -1;

  constructor(
    private storage_svc: SessionStorageService,
    private router: Router,
    private notification_svc: NotificationsService
  ) {}

  initialize(): void {
    const { steps = [], ...rest } =
      this.storage_svc.getContextItem<StoredActivityConfig>(CONFIG_STORAGE_KEY) ?? {};
    this.config = rest;
    this.orders = this.storage_svc.getContextItem<SessionActivityOrder[]>(ORDERS_STORAGE_KEY) ?? [];
    if (this.config?.type?.order_type_id) this.state.type_id = this.config.type.order_type_id;
    this.steps_subject.next(steps);
  }

  displayWarningModal(): boolean {
    return this.state.display_dialog;
  }

  isCartEmpty(): boolean {
    return this.state.cart_empty;
  }

  orderTypeId(): number {
    return this.config?.type?.order_type_id ?? 0;
  }

  activityName(): string {
    return this.config?.type?.activity_display_name ?? '';
  }

  activityCategoryName(): string {
    return this.config?.type?.name ?? '';
  }

  setActivity(activity: ActivityConfig['type']): void {
    this.reset();
    this.config = { type: activity };
    this.state = { ...DEFAULT_STATE, type_id: activity.order_type_id };
  }

  setCountry(country: Exclude<ActivityConfig['country'], undefined>): void {
    this.config = { ...(this.config ?? {}), country };
  }

  setParameter<T extends Exclude<keyof ActivityConfig, 'type' | 'country'>>(
    key: T,
    value: ActivityConfig[T]
  ): void {
    if (!this.config) return;

    this.config = { ...this.config, [key as string]: value };
  }

  parameter(): Partial<ActivityConfig> | undefined;
  parameter<T extends keyof ActivityConfig>(key: T): ActivityConfig[T] | undefined;
  parameter(key?: keyof ActivityConfig) {
    return key ? this.config?.[key] : this.config;
  }

  activityData(all: boolean = false): ActivityDataToDelete {
    const orders: SessionActivityOrder[] = all
      ? [...this.orders]
      : this.orders.filter((order: SessionActivityOrder) => order.type_id === this.state.type_id);

    return {
      cart: !this.state.cart_empty,
      orders
    };
  }

  activeStepIndex(): number {
    return this.active_step_index;
  }

  steps(): ActivityStep[] {
    return this.steps_subject.value;
  }

  currentStep(step_number: number): ActivityStep {
    return this.steps_subject.value[step_number];
  }

  setSteps(steps: ActivityStep[] | undefined): void {
    const normalized: ActivityStep[] = (steps ?? []).map((c, i) => {
      if (c.active) this.active_step_index = i;
      return {
        can_navigate_to: Boolean(this.steps_subject.value[i]?.can_navigate_to),
        ...c,
        active: Boolean(c.active),
        step_url: c.active ? this.router.url : c.step_url
      };
    });
    this.steps_subject.next(normalized);
    this.saveConfig();
  }

  setOrderForPerson(value: PersonItem | undefined): void {
    this.order_for_person.next(value);
  }

  getOrderForPerson(): PersonItem | undefined {
    return this.order_for_person.value;
  }

  setAdminOverrideSupported(
    is_admin_supported: Exclude<ActivityConfig['is_admin_supported'], undefined>
  ): void {
    this.config = { ...(this.config ?? {}), is_admin_supported };
  }

  getAdminOverrideSupported(): boolean | undefined {
    return this.config?.type?.is_admin_supported;
  }

  empty(): void {
    this.config = undefined;
    this.setOrderForPerson(undefined);
    this.steps_subject.next([]);
    this.notification_svc.order_notifications.next([]);
    CartInfo.cart_data = undefined;
    this.saveConfig();
  }

  reset(): void {
    this.empty();
    this.state = { ...DEFAULT_STATE };
  }

  saveConfig(): void {
    if (this.config)
      this.storage_svc.setContextItem<Partial<StoredActivityConfig>>(CONFIG_STORAGE_KEY, {
        ...this.config,
        steps: this.steps_subject.value
      });
    else this.storage_svc.removeContextItems(CONFIG_STORAGE_KEY);
  }

  isValidCart(res: ShoppingCartData | undefined): boolean {
    if (res && res.shopping_cart_items.length > 0 && ![12].includes(res.orderTypeId)) return true;
    return false;
  }

  cartUpdated(cart_data: ShoppingCartData): void {
    // Following discussion with product, display message only if articles are in cart, not if anything concerning
    // the activity was saved in cart or in session storage
    const { orderTypeId, shopping_cart_items, order_for_person, existing_asset } = cart_data;

    if (!orderTypeId) {
      this.state = { ...this.state, cart_empty: true, display_dialog: false };
      return;
    }

    this.state = {
      type_id: orderTypeId,
      cart_empty: false,
      display_dialog: shopping_cart_items.length > 0 || Boolean(existing_asset)
    };
    CartInfo.cart_data = cart_data;
    this.setOrderForPerson(order_for_person);
  }

  registerActivityOrder(id: number, type_id: number): void {
    if (!id || !type_id) return;

    this.orders.push({ type_id, id });
    this.storage_svc.setContextItem<SessionActivityOrder[]>(ORDERS_STORAGE_KEY, this.orders);
  }

  unregisterActivityOrder(data: ActivityDataDeleteResult): void {
    if (data.cart) this.state = { ...DEFAULT_STATE };

    const deleted_orders: Set<number> = data.orders.reduce(
      (acc: Set<number>, order: ActivityDataDeleteOrderResult) => {
        if (order.deleted) acc.add(order.id);
        return acc;
      },
      new Set<number>()
    );

    if (deleted_orders.size > 0) {
      this.orders = this.orders.filter(
        (order: SessionActivityOrder) => !deleted_orders.has(order.id)
      );

      if (this.orders.length > 0)
        this.storage_svc.setContextItem<SessionActivityOrder[]>(ORDERS_STORAGE_KEY, this.orders);
      else this.storage_svc.removeContextItems(ORDERS_STORAGE_KEY);
    }
  }

  private isValueEmpty(value: any): boolean {
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    if (typeof value === 'number' || typeof value === 'boolean') return false;
    return !value;
  }

  private affectToConfig<T extends keyof ActivityConfig>(key: T, value: ActivityConfig[T]): void {
    if (!this.isValueEmpty(this.config?.[key])) return;

    if (!this.config) this.config = {};
    this.config[key] = value;
  }
}
